/**
 * 出勤形態×報酬区分別(⑥指示: 日次／週次／月次を切替)。現行シート「スタッフ別」「原価率」の再現。
 *  - 種別行(品川/時給・在宅/時給・在宅/歩合・管理者なし合計・管理者あり合計・ホワイト・総合計)の
 *    アポ・時間・アポ/h・売上見込・報酬・原価率・目標。見出しクリックで並べ替え、0件の行は隠せる
 *  - 週次: 日別の推移 / 月次: 週(W1〜W6)と日別の推移(横スクロールなしで収まる列数)
 */
import { useMemo, useState } from 'react';
import HelpTip from '@core/components/HelpTip';
import { ChipSelect, SortTh, sortRows, useSort } from '@core/components/tableTools';
import { weekdayLabel } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import { CATEGORY_KEYS, CATEGORY_ROW_LABELS, apphOf, categoryRows, costRate, fmtNum, fmtPct, rateClass, targetFor, weekOfMonth, type CategoryKey } from './calc';
import { HELP, costRateClass, costRateVerdict } from './colors';
import PeriodToolbar from './PeriodToolbar';
import { usePeriodData } from './usePeriod';

type SortKey = 'label' | 'appointments' | 'hours' | 'apph' | 'revenue' | 'pay' | 'rate' | 'target';

export default function StylesSummaryPage() {
  const period = usePeriodData();
  const { rows, targets, loading, range, mode } = period;
  const [hideZero, setHideZero] = useState<'hide' | 'show'>('hide');
  const { sort, toggle: toggleSort } = useSort<SortKey>(null);

  const cats = useMemo(() => categoryRows(rows), [rows]);
  const keys = useMemo(() => {
    const list = CATEGORY_KEYS.filter((k) => hideZero === 'show' || cats[k].appointments > 0 || cats[k].hours > 0);
    if (!targets) return list;
    return sortRows(list, sort, (k, key) => {
      const a = cats[k];
      switch (key) {
        case 'label':
          return CATEGORY_ROW_LABELS[k];
        case 'appointments':
          return a.appointments;
        case 'hours':
          return a.hours;
        case 'apph':
          return apphOf(a);
        case 'revenue':
          return a.revenue;
        case 'pay':
          return a.pay;
        case 'rate':
          return costRate(a);
        case 'target':
          return targetFor(k, targets);
      }
    });
  }, [cats, hideZero, sort, targets]);

  /** 推移の列: 週次=日 / 月次=週 */
  const columns = useMemo(() => {
    if (mode === 'day') return [];
    if (mode === 'week') return range.dates.map((d) => ({ key: d, label: `${Number(d.slice(8))}日${weekdayLabel(d)}`, rows: rows.filter((r) => r.date === d) }));
    const weeks = Array.from(new Set(range.dates.map((d) => weekOfMonth(d))));
    return weeks.map((w) => ({ key: `W${w}`, label: `W${w}`, rows: rows.filter((r) => weekOfMonth(r.date) === w) }));
  }, [mode, range.dates, rows]);
  const columnCats = useMemo(() => columns.map((c) => ({ ...c, cats: categoryRows(c.rows) })), [columns]);

  const rateCell = (v: number | null, target: number | null, bold = false, key?: string) => {
    const verdict = costRateVerdict(v, target ?? 44.5);
    return (
      <td key={key} className={`num ${costRateClass(v)}`} style={bold ? { fontWeight: 700 } : undefined}>
        {verdict ? (
          <HelpTip help={HELP.costRate} verdict={verdict} value={fmtPct(v)}>
            {fmtPct(v)}
          </HelpTip>
        ) : (
          fmtPct(v)
        )}
      </td>
    );
  };

  return (
    <div className="screen master-screen full" style={{ maxWidth: 1400 }}>
      <PeriodToolbar period={period} />
      {loading || !targets ? (
        <div className="card">
          <div className="empty">読み込み中…</div>
        </div>
      ) : rows.length === 0 ? (
        <div className="card">
          <div className="empty">{range.label} の集計データがありません。</div>
        </div>
      ) : (
        <>
          <div className="card">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              出勤形態×報酬区分別({range.label})
              <span style={{ marginLeft: 'auto' }}>
                <ChipSelect
                  options={[
                    { key: 'hide', label: '0件の行を隠す' },
                    { key: 'show', label: '全ての行' },
                  ]}
                  value={hideZero}
                  onChange={(v) => setHideZero(v === 'show' ? 'show' : 'hide')}
                  allLabel={null}
                />
              </span>
            </h3>
            <div className="table-scroll">
            <table className="fit-table">
              <thead>
                <tr>
                  <SortTh label="出勤形態" sortKey="label" sort={sort} onSort={toggleSort} defaultDir="asc" />
                  <SortTh label="アポ" sortKey="appointments" sort={sort} onSort={toggleSort} className="num" />
                  <SortTh label="時間(h)" sortKey="hours" sort={sort} onSort={toggleSort} className="num" />
                  <SortTh label="アポ/h" sortKey="apph" sort={sort} onSort={toggleSort} className="num" help={HELP.apph} />
                  <SortTh label="売上見込" sortKey="revenue" sort={sort} onSort={toggleSort} className="num" help={HELP.revenue} />
                  <SortTh label="報酬(原価)" sortKey="pay" sort={sort} onSort={toggleSort} className="num" />
                  <SortTh
                    label="原価率"
                    sortKey="rate"
                    sort={sort}
                    onSort={toggleSort}
                    className="num"
                    defaultDir="asc"
                    help={{ ...HELP.costRate, what: '報酬 ÷ 売上見込。目標(右の列)以内なら緑字、超えると赤字。帯色は原価率シートと同じ' }}
                  />
                  <SortTh label="目標" sortKey="target" sort={sort} onSort={toggleSort} className="num" />
                </tr>
              </thead>
              <tbody>
                {keys.map((k: CategoryKey) => {
                  const a = cats[k];
                  const t = targetFor(k, targets);
                  const bold = k === 'no_manager' || k === 'with_manager' || k === 'total';
                  const v = costRate(a);
                  const verdict = costRateVerdict(v, t ?? targets.cost_rate_total);
                  return (
                    <tr key={k} style={bold ? { fontWeight: 700, background: '#F7F9FD' } : undefined}>
                      <td>{CATEGORY_ROW_LABELS[k]}</td>
                      <td className="num">{a.appointments}</td>
                      <td className="num">{fmtNum(a.hours, 1)}</td>
                      <td className="num">{fmtNum(apphOf(a))}</td>
                      <td className="num">¥{formatYen(a.revenue)}</td>
                      <td className="num">¥{formatYen(a.pay)}</td>
                      <td className={`num ${rateClass(v, t)}`}>
                        {verdict ? (
                          <HelpTip help={HELP.costRate} verdict={verdict} value={fmtPct(v)}>
                            {fmtPct(v)}
                          </HelpTip>
                        ) : (
                          fmtPct(v)
                        )}
                      </td>
                      <td className="num hint">{t === null ? '' : `${t}%`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>

          {columnCats.length > 0 && (
            <div className="card">
              <h3>
                原価率の推移({mode === 'week' ? '日別' : '週別 W1〜W6・月曜始まり'})
              </h3>
              <div className="table-scroll">
              <table className="fit-table">
                <thead>
                  <tr>
                    <th>出勤形態</th>
                    <th className="num">期間累計</th>
                    {columnCats.map((c) => (
                      <th key={c.key} className="num">
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k}>
                      <td>{CATEGORY_ROW_LABELS[k]}</td>
                      {rateCell(costRate(cats[k]), targetFor(k, targets), true)}
                      {columnCats.map((c) => rateCell(costRate(c.cats[k]), targetFor(k, targets), false, c.key))}
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <h3 style={{ marginTop: 18 }}>取得数の推移</h3>
              <div className="table-scroll">
              <table className="fit-table">
                <thead>
                  <tr>
                    <th>出勤形態</th>
                    <th className="num">期間累計</th>
                    {columnCats.map((c) => (
                      <th key={c.key} className="num">
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k}>
                      <td>{CATEGORY_ROW_LABELS[k]}</td>
                      <td className="num" style={{ fontWeight: 700 }}>
                        {cats[k].appointments}
                      </td>
                      {columnCats.map((c) => (
                        <td key={c.key} className="num">
                          {c.cats[k].appointments}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
